import { useCallback, useEffect, useRef, useState } from 'react'

import type { AuthError } from '../../shared/auth.ts'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The whole error body. Auth screens need `kind`, `attemptsRemaining`, `retryAfterS`. */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    // The session is an httpOnly cookie, so it has to ride along with every request.
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  const body = text ? JSON.parse(text) : null
  if (!res.ok) {
    // A session can expire while someone is sitting on a screen. Every request notices,
    // so the app returns them to sign-in instead of showing a page of load errors.
    if (res.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED))
    }
    throw new ApiError(
      body?.error ?? body?.message ?? `Request failed (${res.status})`,
      res.status,
      body ?? {},
    )
  }
  return body as T
}

export const SESSION_EXPIRED = 'ks:session-expired'

/**
 * Normalise anything thrown by a request into the shape the auth screens branch on.
 * A network failure has no `kind`, so it lands on the generic message path.
 */
export function asAuthError(err: unknown): AuthError {
  if (err instanceof ApiError) {
    const body = err.body as Partial<AuthError>
    return { ...body, error: body.error ?? err.message }
  }
  return { error: err instanceof Error ? err.message : String(err) }
}

export const get = <T,>(path: string) => request<T>(path)
export const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) })
export const put = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) })

export interface Resource<T> {
  data: T | null
  error: string | null
  /** True only on the first load. A refresh must not blank a screen that already has data. */
  loading: boolean
  refreshing: boolean
  reload: () => void
}

/**
 * Fetch and keep fresh.
 *
 * `pollMs` matters here: a trading screen that silently shows minute-old prices is worse
 * than one that says it cannot see the market. The freshness indicator in the shell tells
 * the user which they are looking at.
 */
export function useApi<T>(path: string | null, pollMs = 0): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(path !== null)
  const [refreshing, setRefreshing] = useState(false)
  const seen = useRef(false)

  const load = useCallback(
    async (quiet: boolean) => {
      if (path === null) return
      if (quiet) setRefreshing(true)
      try {
        const next = await get<T>(path)
        setData(next)
        setError(null)
        seen.current = true
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    },
    [path],
  )

  useEffect(() => {
    seen.current = false
    setLoading(path !== null)
    void load(false)
  }, [load, path])

  useEffect(() => {
    if (!pollMs || path === null) return
    const id = setInterval(() => void load(true), pollMs)
    return () => clearInterval(id)
  }, [load, pollMs, path])

  return { data, error, loading, refreshing, reload: () => void load(true) }
}
